// Pattern: docs/design-patterns/frontend/component-patterns.md (layout-structure-only)
// Pattern: docs/design-patterns/frontend/accessibility.md (nav landmark, aria-expanded toggle)
// Pattern: docs/design-patterns/frontend/responsive-design.md (44px touch targets)
// Pattern: docs/design-patterns/frontend/state-management.md (narrow persisted UI slice)
//
// Persistent global left sidebar. Layout container: reads isNavSidebarCollapsed
// from uiStore (self-wired — no props) and composes SidebarNav + the footer
// (ApiStatus + collapse toggle). ApiStatus's rail-mode status dot is read
// here directly (useApiStatus) rather than adding a `compact` prop's worth
// of duplicated logic beyond what already lives on ApiStatus — the footer
// swaps between the full <ApiStatus/> (expanded) and a compact icon-only
// rendering of the same query (collapsed), matching B6.6 ("footer ApiStatus
// collapses to icon/status-dot only in rail mode").
// OrgSwitcher (FE-003) sits between the logo and the nav ScrollArea, self-
// contained the same way it was in TopBar — it reads useTenant() itself, so
// Sidebar only gates *whether* it renders (expanded + multi-org), not what
// it renders.
import type { JSX } from 'react';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { useTenant } from '~/hooks/useTenant';
import { useUIStore } from '~/stores/uiStore';

import { ApiStatus } from '~/components/ApiStatus';
import { Logo } from '~/components/Logo';
import { OrgSwitcher } from '~/components/OrgSwitcher';
import { ScrollArea } from '~/components/ScrollArea';

import { getNavSections } from './navConfig';
import { SidebarNav } from './SidebarNav';

export function Sidebar(): JSX.Element {
  const collapsed = useUIStore(state => state.isNavSidebarCollapsed);
  const toggleCollapsed = useUIStore(state => state.toggleNavSidebar);
  const { organizations } = useTenant();
  const sections = getNavSections();

  return (
    <nav
      aria-label="Primary"
      className={clsx(
        'flex h-full shrink-0 flex-col border-r border-default bg-surface transition-[width] duration-200',
        collapsed ? 'w-[68px]' : 'w-64',
      )}
    >
      <div
        className={clsx(
          'flex h-16 shrink-0 items-center',
          collapsed ? 'justify-center px-2' : 'px-4',
        )}
      >
        <Logo iconOnly={collapsed} />
      </div>

      {!collapsed && organizations && organizations.length > 1 && (
        <div className="border-b border-default px-4 py-3">
          <OrgSwitcher />
        </div>
      )}

      <ScrollArea className="min-h-0 flex-1" padding={false}>
        <div className="py-2">
          <SidebarNav sections={sections} collapsed={collapsed} />
        </div>
      </ScrollArea>

      <div
        className={clsx(
          'flex shrink-0 items-center gap-2 border-t border-default py-3',
          collapsed ? 'flex-col' : 'justify-between px-4',
        )}
      >
        <ApiStatus compact={collapsed} />
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="flex h-11 w-11 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-elevated hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </div>
    </nav>
  );
}
